import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, TrophyIcon, CalendarIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { DefaultService, PlayerWithLeagueContext, RatingHistory, PlayOutput, PlayerLeagueMembership } from '../generated-api';
import ImageCameraDialog from '../components/common/ImageCameraDialog';
import PlayerRatingsChart from '../components/common/PlayerRatingsChart';
import { getRatingHistories } from '../services/ratingHistories';
import PlayerAvatar from '../components/common/PlayerAvatar';
import ImageModal from '../components/common/ImageModal';
import ActionButtons from '../components/common/ActionButtons';
import PageHeader from '../components/common/PageHeader';
import Tooltip from '../components/common/Tooltip';
import { useGames } from '../contexts/DataContext';


const PlayerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { games, fetch: fetchGames } = useGames();
  const [player, setPlayer] = useState<PlayerWithLeagueContext | null>(null);
  const [allPlayerPlays, setAllPlayerPlays] = useState<PlayOutput[]>([]);
  const [playerLeagues, setPlayerLeagues] = useState<PlayerLeagueMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editModeKey, setEditModeKey] = useState(0); // Counter to force ImageCameraDialog remount
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);
  const [leagueRatingHistories, setLeagueRatingHistories] = useState<Record<string, RatingHistory>>({});
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showAllPlays, setShowAllPlays] = useState(false);

  useEffect(() => {
    const loadPlayerData = async () => {
      if (!id) return;

      try {
        setError(null);
        setLeagueRatingHistories({});
        const [playerData, , playerPlays, playerLeagueMemberships] = await Promise.all([
          DefaultService.getPlayerPlayersPlayerIdGet({ playerId: id }),
          fetchGames(), // Use context-provided fetch
          DefaultService.getPlaysPlaysGet({ playerId: id }), // Use server-side filtering by playerId
          DefaultService.getPlayerLeaguesPlayersPlayerIdLeaguesGet({ playerId: id })
        ]);

        setPlayer(playerData);
        setAllPlayerPlays(playerPlays);
        setPlayerLeagues(playerLeagueMemberships);
        setEditName(playerData.name);

        const filteredLeagues = playerLeagueMemberships.filter(l => l.leagueName !== 'Unranked');

        // Set initial selected league to Global league
        const globalLeague = playerLeagueMemberships.find(l => l.isGlobal);
        if (globalLeague) {
          setSelectedLeagueId(globalLeague.leagueId);
        } else if (filteredLeagues.length > 0) {
          setSelectedLeagueId(filteredLeagues[0].leagueId);
        }

      } catch (err) {
        setError('Failed to load player data. Please try again.');
        console.error('Error loading player:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPlayerData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only the selected league's history is on screen, so fetch each league when it's selected
  const selectedLeagueName = playerLeagues.find(l => l.leagueId === selectedLeagueId)?.leagueName;
  useEffect(() => {
    if (!id || !selectedLeagueId || !selectedLeagueName || selectedLeagueName === 'Unranked') return;

    let cancelled = false;
    getRatingHistories(selectedLeagueId, { playerIds: [id] })
      .then(histories => {
        if (!cancelled && histories[id]) {
          setLeagueRatingHistories(prev => ({ ...prev, [selectedLeagueId]: histories[id] }));
        }
      })
      .catch(err => console.error(`Failed to load history for league ${selectedLeagueName}:`, err));

    return () => {
      cancelled = true;
    };
  }, [id, selectedLeagueId, selectedLeagueName]);

  const handleUpdatePlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !editName.trim()) return;

    setUpdating(true);
    try {
      const updatedPlayer = await DefaultService.updatePlayerPlayersPlayerIdPut({ playerId: id, requestBody: {
        name: editName.trim()
      } });
      
      // Upload image separately if provided and use the response to update player state
      let finalPlayer = updatedPlayer;
      if (editImage) {
        const playerWithImage = await DefaultService.uploadPlayerImagePlayersPlayerIdImagePost({ playerId: id, formData: { image: editImage } });
        finalPlayer = playerWithImage;
      }
      setPlayer({ ...player!, ...finalPlayer });
      setEditing(false);
      setEditImage(null);
    } catch (err) {
      setError((err as any)?.body?.detail || (err as Error).message || 'Failed to update player. Please try again.');
      console.error('Error updating player:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePlayer = async () => {
    if (!id || !player) return;
    
    if (!window.confirm(`Are you sure you want to delete ${player.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await DefaultService.deletePlayerPlayersPlayerIdDelete({ playerId: id });
      navigate('/players');
    } catch (err) {
      setError('Failed to delete player. Please try again.');
      console.error('Error deleting player:', err);
    }
  };

  const handleImageSelected = (file: File, preview: string) => {
    setEditImage(file);
  };

  const getRatingChangeForPlay = (playId: string, leagueId: string): number | null => {
    const history = leagueRatingHistories[leagueId];
    if (!history?.ratingProgression) return null;

    const progressionEntry = history.ratingProgression.find(
      entry => entry.playId === playId
    );

    return progressionEntry ? progressionEntry.ratingChange : null;
  };

  // Get plays filtered by selected league
  const getLeaguePlays = (leagueId: string): PlayOutput[] => {
    return allPlayerPlays.filter(play => play.leagueIds.includes(leagueId));
  };

  // Get selected league data
  const selectedLeague = playerLeagues.find(l => l.leagueId === selectedLeagueId);

  // Get filtered leagues (excluding Unranked)
  const filteredLeagues = playerLeagues.filter(l => l.leagueName !== 'Unranked');

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-heading mb-4">Player Not Found</h2>
        <p className="text-body mb-6">The player you're looking for doesn't exist.</p>
        <Link
          to="/players"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Players
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-8">
      {/* Header */}
      <PageHeader className="mb-8 px-4 sm:px-0">
        <Link to="/players" className="text-primary-500 hover:text-primary-700 mb-4 inline-block">
          ← Back to Players
        </Link>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-fluid-h1 font-bold text-heading font-serif" data-testid="player-name-heading">{player.name}</h1>
          {!editing && (
            <ActionButtons
              onEdit={() => {
                setEditing(true);
                setEditModeKey(prev => prev + 1);
              }}
              onDelete={handleDeletePlayer}
              className="flex-row"
            />
          )}
        </div>
      </PageHeader>

      {/* Error Message */}
      {error && (
        <div className="mx-4 sm:mx-0 mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Player Info Card */}
        <div className="lg:col-span-1">
          <div className="bg-surface p-4 sm:shadow sm:rounded-xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex justify-center">
                <button data-track="player_image_open" onClick={() => player && player.imageUrl && setIsImageModalOpen(true)} className="focus:outline-none">
                  <PlayerAvatar name={player.name} imageUrl={player.imageUrl} size="xxlarge" />
                </button>
              </div>
              
              {editing ? (
                <form onSubmit={handleUpdatePlayer} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="text-center text-xl font-bold text-heading w-full border border-edge-input rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  <ImageCameraDialog
                    key={`image-dialog-${editModeKey}`}
                    onImageSelected={handleImageSelected}
                    label="Player Image (Optional)"
                  />
                  <div className="flex space-x-2 justify-center">
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-3 py-1 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {updating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false);
                        setEditName(player.name);
                        setEditImage(null);
                      }}
                      className="px-3 py-1 bg-surface-skeleton text-body text-sm rounded-md hover:bg-surface-active"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-muted">Player since {new Date(player.createdAt).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stats and Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* League Tabs */}
          {filteredLeagues.length > 0 && (
            <div className="bg-surface sm:shadow sm:rounded-xl overflow-hidden">
              {/* Tab Navigation */}
              <div className="border-b border-edge">
                <nav className="-mb-px flex space-x-4 px-4 sm:px-6 overflow-x-auto" aria-label="Leagues">
                  {filteredLeagues
                    .sort((a, b) => {
                      // Sort Global league first, then alphabetically
                      if (a.isGlobal) return -1;
                      if (b.isGlobal) return 1;
                      return a.leagueName.localeCompare(b.leagueName);
                    })
                    .map((league) => (
                      <button
                        data-track="player_league_tab"
                        key={league.leagueId}
                        onClick={() => setSelectedLeagueId(league.leagueId)}
                        className={`
                          whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                          ${selectedLeagueId === league.leagueId
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-muted hover:text-body hover:border-edge-input'
                          }
                        `}
                      >
                        <span className="text-lg">{league.leagueIcon || (league.isGlobal ? '🌍' : '🏆')}</span>
                        <span>{league.leagueName}</span>
                      </button>
                    ))}
                </nav>
              </div>

              {/* Tab Content */}
              {selectedLeague && selectedLeagueId && (
                <div className="px-4 py-5 sm:p-4 space-y-6 sm:space-y-4">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    <div className="bg-surface-base sm:bg-surface rounded-xl p-2.5 sm:p-4 sm:border sm:border-edge text-center">
                      <div className="text-fluid-stat font-bold font-mono text-green-600">
                        {getLeaguePlays(selectedLeagueId).length}
                      </div>
                      <div className="text-xs sm:text-sm text-muted">Play Sessions</div>
                    </div>

                    <div className="bg-surface-base sm:bg-surface rounded-xl p-2.5 sm:p-4 sm:border sm:border-edge text-center">
                      <div className="text-fluid-stat font-bold font-mono text-primary-600">
                        {selectedLeague.rating.toFixed(2)}
                      </div>
                      <div className="text-xs sm:text-sm font-mono text-purple-600 font-medium">
                        ±{(Math.round(selectedLeague.sigma * 100) / 100).toFixed(2)}
                      </div>
                      <div className="text-xs sm:text-sm text-muted">Rating</div>
                    </div>

                    <div className="bg-surface-base sm:bg-surface rounded-xl p-2.5 sm:p-4 sm:border sm:border-edge text-center">
                      <div className="text-fluid-stat font-bold font-mono text-muted">
                        {(selectedLeague.rating - 3 * selectedLeague.sigma).toFixed(2)}
                      </div>
                      <div className="text-xs sm:text-sm text-muted flex items-center justify-center gap-1">
                        <span className="hidden sm:inline">Conservative</span>
                        <span className="sm:hidden">Cons.</span>
                        <Tooltip
                          trigger={
                            <button
                              type="button"
                              className="text-disabled hover:text-body"
                              aria-label="Conservative rating explanation"
                            >
                              <InformationCircleIcon className="h-3.5 w-3.5" />
                            </button>
                          }
                          content={
                            <>
                              <p className="leading-relaxed">
                                Conservative rating is calculated as: <strong>Rating - (3 × Sigma)</strong>
                              </p>
                              <p className="mt-2 leading-relaxed">
                                This represents a 99.7% confidence lower bound. It's useful for comparing players with different levels of rating certainty.
                              </p>
                            </>
                          }
                          hover={false}
                          showCloseButton={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Game Stats */}
                  <div className="sm:border sm:border-edge sm:rounded-xl sm:p-4">
                    <h3 className="text-lg font-medium text-heading mb-3 flex items-center">
                      <TrophyIcon className="h-5 w-5 mr-2" />
                      Game Stats
                    </h3>
                    {(() => {
                      const leaguePlays = getLeaguePlays(selectedLeagueId);

                      if (!leaguePlays || leaguePlays.length === 0) {
                        return (
                          <div className="text-center py-4 text-muted">
                            No games played yet.
                          </div>
                        );
                      }

                      // Count games played
                      const gameCount: Record<string, number> = {};
                      leaguePlays.forEach(play => {
                        gameCount[play.gameId] = (gameCount[play.gameId] || 0) + 1;
                      });
                      const mostPlayedGameId = Object.entries(gameCount)
                        .sort(([,a], [,b]) => b - a)[0]?.[0];
                      const mostPlayedGame = mostPlayedGameId ? games.find(g => g.id === mostPlayedGameId) : null;
                      const mostPlayedCount = mostPlayedGameId ? gameCount[mostPlayedGameId] : 0;

                      // Count wins by game and total
                      const gameWins: Record<string, number> = {};
                      let totalWins = 0;
                      leaguePlays.forEach(play => {
                        let playerPosition = 1;
                        let foundPlayer = false;
                        for (let i = 0; i < play.ranking.length && !foundPlayer; i++) {
                          const rankItem = play.ranking[i];
                          if (typeof rankItem === 'string') {
                            const teamPlayerIds = play.teams[rankItem] || [];
                            if (teamPlayerIds.includes(player!.id)) {
                              foundPlayer = true;
                            } else {
                              playerPosition += teamPlayerIds.length;
                            }
                          } else {
                            let tieSize = 0;
                            let playerInTie = false;
                            for (const teamName of rankItem) {
                              const teamPlayerIds = play.teams[teamName] || [];
                              tieSize += teamPlayerIds.length;
                              if (teamPlayerIds.includes(player!.id)) {
                                playerInTie = true;
                              }
                            }
                            if (playerInTie) {
                              foundPlayer = true;
                            } else {
                              playerPosition += tieSize;
                            }
                          }
                        }
                        if (playerPosition === 1) {
                          totalWins++;
                          gameWins[play.gameId] = (gameWins[play.gameId] || 0) + 1;
                        }
                      });

                      const mostWonGameId = Object.entries(gameWins)
                        .sort(([,a], [,b]) => b - a)[0]?.[0];
                      const mostWonGame = mostWonGameId ? games.find(g => g.id === mostWonGameId) : null;
                      const mostWonCount = mostWonGameId ? gameWins[mostWonGameId] : 0;
                      const winRate = Math.round((totalWins / leaguePlays.length) * 100);

                      return (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted">Most Played</span>
                            <span className="text-sm font-medium text-heading">
                              {mostPlayedGame?.name || 'N/A'} ({mostPlayedCount}x)
                            </span>
                          </div>
                          <div className="border-t border-edge-subtle" />
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted">Most Won</span>
                            <span className="text-sm font-medium text-heading">
                              {mostWonGame ? `${mostWonGame.name} (${mostWonCount}x)` : 'No wins yet'}
                            </span>
                          </div>
                          <div className="border-t border-edge-subtle" />
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted">Total Wins</span>
                            <span className="text-sm font-medium text-green-600">
                              {totalWins} {totalWins === 1 ? 'victory' : 'victories'} ({winRate}%)
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Recent Play Sessions */}
                  <div className="sm:border sm:border-edge sm:rounded-xl sm:p-4">
                    <h3 className="text-lg font-medium text-heading mb-4 flex items-center">
                      <CalendarIcon className="h-5 w-5 mr-2" />
                      Recent Play Sessions
                    </h3>
                    {(() => {
                      const leaguePlays = getLeaguePlays(selectedLeagueId);

                      if (!leaguePlays || leaguePlays.length === 0) {
                        return (
                          <p className="text-muted text-center py-4">
                            No play sessions yet.{' '}
                            <Link to="/plays/new" className="text-primary-600 hover:text-primary-500">
                              Record a play session
                            </Link>
                          </p>
                        );
                      }

                      const displayedPlays = showAllPlays ? leaguePlays : leaguePlays.slice(0, 3);

                      return (
                        <div className="divide-y divide-edge-subtle sm:divide-y-0 sm:space-y-2">
                          {displayedPlays.map((play, index) => {
                            const game = games.find(g => g.id === play.gameId);
                            const allPlayerIds = Object.values(play.teams).flat();

                            // Find player's position in the ranking
                            let playerPosition = 1;
                            let foundPlayer = false;
                            for (let i = 0; i < play.ranking.length && !foundPlayer; i++) {
                              const rankItem = play.ranking[i];
                              if (typeof rankItem === 'string') {
                                // Single team/player at this position
                                const teamPlayerIds = play.teams[rankItem] || [];
                                if (teamPlayerIds.includes(player!.id)) {
                                  foundPlayer = true;
                                } else {
                                  playerPosition += teamPlayerIds.length;
                                }
                              } else {
                                // Tie (multiple teams/players)
                                let tieSize = 0;
                                let playerInTie = false;
                                for (const teamName of rankItem) {
                                  const teamPlayerIds = play.teams[teamName] || [];
                                  tieSize += teamPlayerIds.length;
                                  if (teamPlayerIds.includes(player!.id)) {
                                    playerInTie = true;
                                  }
                                }
                                if (playerInTie) {
                                  foundPlayer = true;
                                } else {
                                  playerPosition += tieSize;
                                }
                              }
                            }

                            return (
                              <Link
                                data-track="player_play_card"
                                key={play.id}
                                to={`/plays/${play.id}`}
                                className="block py-3 sm:border sm:border-edge sm:rounded-xl sm:p-4 hover:bg-surface-hover sm:hover:border-primary-300 transition-all active:scale-[0.98]"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-sm font-medium text-heading truncate flex-1 min-w-0">
                                    {game?.name || 'Unknown Game'}
                                  </h4>
                                  {/* Results */}
                                  <div className="text-right flex-shrink-0 ml-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      playerPosition === 1
                                        ? 'bg-amber-100 text-amber-900'
                                        : playerPosition === 2
                                        ? 'bg-stone-200 text-stone-700'
                                        : playerPosition === 3
                                        ? 'bg-orange-100 text-orange-800'
                                        : 'bg-stone-100 text-stone-600'
                                    }`}>
                                      #{playerPosition} place
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                  {/* Game Image - hidden on mobile */}
                                  <div className="flex-shrink-0 hidden sm:block">
                                    {game?.imageUrl ? (
                                      <img
                                        src={game.imageUrl}
                                        alt={game.name}
                                        className="w-16 h-16 rounded-lg object-contain"
                                      />
                                    ) : (
                                      <div className="w-16 h-16 rounded-lg bg-surface-skeleton flex items-center justify-center">
                                        <span className="text-xs text-muted text-center px-1">
                                          {game?.name || 'Game'}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Play Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs text-muted mb-2 truncate">
                                          <span className="sm:hidden">{new Date(play.playDate).toLocaleDateString()}</span>
                                          <span className="hidden sm:inline">{new Date(play.playDate).toLocaleDateString()} at {new Date(play.playDate).toLocaleTimeString()}</span>
                                        </p>

                                        {/* Participants */}
                                        <div className="text-xs text-body mb-2 truncate">
                                          <span className="font-medium">{allPlayerIds.length} players</span>
                                        </div>

                                        {/* Extensions */}
                                        {play.gameExtensions && play.gameExtensions.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mb-2 overflow-hidden max-h-12">
                                            {play.gameExtensions.map((extension, idx) => (
                                              <span key={idx} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 truncate max-w-full">
                                                {extension}
                                              </span>
                                            ))}
                                          </div>
                                        )}

                                        {/* Comment - Hidden on mobile */}
                                        {play.comment && (
                                          <p className="hidden sm:block text-xs text-body italic mt-1 line-clamp-2 break-words">
                                            "{play.comment}"
                                          </p>
                                        )}
                                      </div>

                                      {/* Rating Change */}
                                      <div className="text-right flex-shrink-0">
                                          <p className="text-xs text-muted">Rating change</p>
                                          {(() => {
                                            const ratingChange = getRatingChangeForPlay(play.id, selectedLeagueId);
                                            if (ratingChange === null) {
                                              return (
                                                <p className="text-sm font-medium text-muted">
                                                  N/A
                                                </p>
                                              );
                                            }

                                            const roundedChange = Math.round(ratingChange * 100) / 100;
                                            const arrow = roundedChange > 0 ? '▲ ' : roundedChange < 0 ? '▼ ' : '';
                                            const displayValue = roundedChange > 0 ? `+${roundedChange}` : `${roundedChange}`;

                                            return (
                                              <p className={`text-sm font-medium ${
                                                roundedChange > 0
                                                  ? 'text-green-600'
                                                  : roundedChange < 0
                                                  ? 'text-red-600'
                                                  : 'text-body'
                                              }`}>
                                                {arrow}{displayValue}
                                              </p>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                              </Link>
                            );
                          })}
                          {leaguePlays.length > 3 && (
                            <button
                              data-track="player_show_all_plays"
                              onClick={() => setShowAllPlays(!showAllPlays)}
                              className="w-full text-center text-sm text-primary-600 hover:text-primary-800 py-2"
                            >
                              {showAllPlays ? 'Show less' : `Show all ${leaguePlays.length} plays`}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Rating History */}
                  {selectedLeagueId && leagueRatingHistories[selectedLeagueId] && (
                    <PlayerRatingsChart
                      players={[{
                        id: selectedLeague!.leagueId,
                        name: selectedLeague!.leagueName,
                        imageUrl: selectedLeague!.leagueIcon,
                        createdAt: player?.createdAt || '',
                        leagueRating: {
                          playerId: player!.id,
                          leagueId: selectedLeague!.leagueId,
                          rating: selectedLeague!.rating,
                          sigma: selectedLeague!.sigma,
                          gamesPlayed: selectedLeague!.gamesPlayed,
                          lastUpdated: selectedLeague!.lastUpdated,
                          createdAt: player?.createdAt || '',
                          isRatingUncertain: (selectedLeague!.gamesPlayed < 5) || (selectedLeague!.sigma > 5)
                        }
                      }]}
                      playerHistories={{ [selectedLeagueId]: leagueRatingHistories[selectedLeagueId] }}
                      title={`Rating History`}
                      emptyMessage={`No play sessions yet in this league.`}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={player.imageUrl!}
        altText={player.name}
      />
    </div>
  );
};

export default PlayerDetailPage;
